import { FunnelId } from 'constants/funnel';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { formatCurrency } from 'utils/currency';
import Image from 'components/ui/Image';
import Icon from 'components/ui/Icon';
import Reviews from 'components/Shared/Reviews/Reviews';
import ConversionBar from 'components/Shared/ConversionBar';
import About from './About';
import BeforeAfterPhotosSection from './BeforeAfterPhotosSection';
import Clinic from './Clinic';
import FAQ from './Faq';
import Header from './Header';
import PackagesContainer from './PackagesContainer';
import QunoscoreSection from './QunoscoreSection';
import StructuredData from './StructuredData';
import type { Accommodation } from 'types/Quote';
import type { Image as ImageType } from 'types/Utilities';
import type { ReviewItem, ReviewStats } from 'types/Review';
import type {
  IBlockCtaFields,
  ICityFields,
  IClinicFields,
  ICountryFields,
  ICvFields,
  IDoctorProfileFields,
  IFaqFields,
  ILanguagesFields,
  IPackagesFields,
  IStaffTreatmentFields,
  ITreatmentFields,
} from 'types/contentful/generated';
import type { ClinicTreatmentEntry } from 'types/Clinic';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';

export type ClinicEntry = Omit<
  IClinicFields,
  'photos' | 'defaultAccommodation'
> & {
  photos: {
    items: ImageType[];
  };
  defaultAccommodation: Accommodation;
};

export type PackagesCollection = {
  items: IPackagesFields[];
};

export type StartPackage = { name: string; price: number };

type StaffTreatment = Omit<
  IStaffTreatmentFields,
  'packages' | 'languagesSpoken' | 'treatment'
> & {
  packages: PackagesCollection;
  languagesSpoken: {
    items: ILanguagesFields[];
  };
  treatment: ITreatmentFields;
};

export type DoctorProfileEntry = Omit<
  IDoctorProfileFields,
  | 'doctorImage'
  | 'clinic'
  | 'city'
  | 'staffTreatment'
  | 'cv'
  | 'faq'
  | 'defaultAccommodation'
  | 'cta'
  | 'qunoscoreDetails'
> & {
  doctorImage: ImageType;
  clinic: ClinicEntry;
  city: Omit<ICityFields, 'country'> & {
    country: ICountryFields;
  };
  staffTreatment: {
    items: StaffTreatment[];
  };
  cv?: {
    items: ICvFields[];
  };
  faq?: {
    items: IFaqFields[];
  };
  cta: IBlockCtaFields;
  qunoscoreDetails: {
    qunoscore: {
      'en-US': number;
    };
    qunoscorePoints: {
      'en-US': number;
    };
    topRankedUniversity: {
      'en-US': boolean;
    };
    treatmentsPerformed: {
      'en-US': number;
    };
    TPCAllTime: {
      'en-US': number;
    };
    treatmentsPerformedLastYear: {
      'en-US': number;
    };
    TPCLastYear: {
      'en-US': number;
    };
    yearsOfExperienceAsDoctor: {
      'en-US': number;
    };
    relevantSpecialization: {
      'en-US': boolean;
    };
    scientificActivity: {
      'en-US': boolean;
    };
    specialistSociety: {
      'en-US': boolean;
    };
    appointmentAvailability: {
      'en-US': string;
    };
    responsiveness: {
      'en-US': string;
    };
    englishSpoken: {
      'en-US': string;
    };
    priceLevel: {
      'en-US': string;
    };
    discountForFollowUpTreatment: {
      'en-US': boolean;
    };
    discountForCompanion: {
      'en-US': boolean;
    };
    accommodationIncluded: {
      'en-US': boolean;
    };
    afterCareIncluded: {
      'en-US': boolean;
    };
    qunoReviews: {
      'en-US': number;
    };
    publicReviews: {
      'en-US': number;
    };
  };
};

type Props = {
  entry: DoctorProfileEntry;
  reviewStats: ReviewStats;
  resultState: ReviewItem[];
  beforeAfterPhotos: APIBeforeAfterPhoto[];
  baPhotosTotalCount: string | number;
  bestReview: Record<string, string>;
};

const sortPackages = (packages: PackagesCollection): IPackagesFields[] => {
  if (!packages) {
    return [];
  }

  const sortedItems = packages.items
    .filter(Boolean)
    .sort((a, b) => a.price - b.price);

  return sortedItems;
};

const DoctorProfile = ({
  entry,
  reviewStats,
  resultState,
  beforeAfterPhotos,
  baPhotosTotalCount,
  bestReview,
}: Props): JSX.Element => {
  const { disableReviews, staffTreatment } = entry;
  const { packages } = staffTreatment.items[0] || {};
  const reviewSectionRef = useRef(null);
  const [, setHasReviewsLoaded] = useState(false);
  const { locale, t } = useTranslation();
  const sortedPackages = sortPackages(packages);
  const funnelId = entry.funnel ? entry.funnel.sys.id : FunnelId.CONTACT_FORM;
  const qunoscore = entry?.qunoscoreDetails?.qunoscore?.['en-US'];

  let startPackage: StartPackage | null = null;
  if (sortedPackages.length > 0 && sortedPackages[0]) {
    const { deal, discountedPriceEuros, name, price } = sortedPackages[0];

    startPackage = {
      name,
      price: (deal && discountedPriceEuros) || price,
    };
  }

  useEffect(() => {
    setHasReviewsLoaded(true);
  }, [reviewSectionRef]);

  const shouldReviewsBeVisible =
    !disableReviews &&
    reviewStats &&
    !!reviewStats.total &&
    !!reviewStats.avg_rating;

  return (
    <>
      <div className="pb-24">
        <StructuredData
          entry={entry}
          reviewStats={reviewStats}
          locale={locale}
          review={bestReview}
        />
        <Header
          entry={entry}
          reviewStats={reviewStats}
          startPackage={startPackage}
          funnelId={funnelId}
        />
        <div className="bg-white">
          <About entry={entry} />
          {!disableReviews && !!resultState?.length && (
            <div
              id="reviews"
              className="border-t border-silver-light md:pt-0 mb-20"
            >
              <div className="max-w-screen-xl mx-auto px-0">
                <Reviews
                  stats={reviewStats}
                  reviews={resultState}
                  title={`${[entry.title, entry.lastName]
                    .filter(Boolean)
                    .join(' ')} ${t('reviews')}`}
                  noGrid
                />
              </div>
            </div>
          )}
          {!!beforeAfterPhotos?.length && (
            <BeforeAfterPhotosSection
              beforeAfterPhotos={beforeAfterPhotos}
              entry={entry}
              baPhotosTotalCount={baPhotosTotalCount}
            />
          )}
          {!!qunoscore && <QunoscoreSection entry={entry} />}
        </div>
        {!!entry.staffTreatment?.items.length && (
          <div
            id="packages"
            className={clsx((!reviewStats || !resultState) && 'mb-20')}
          >
            <PackagesContainer
              entry={
                entry.staffTreatment.items as unknown as ClinicTreatmentEntry[]
              }
              itineraryText={entry.clinic?.itineraryText}
              accommodation={entry?.clinic?.defaultAccommodation}
            />
          </div>
        )}
        {entry.clinic && <Clinic clinic={entry.clinic} />}
        {entry.faq && <FAQ items={entry.faq.items} />}
      </div>
      <ConversionBar hideContactButton funnelId={funnelId}>
        <div className="md:flex justify-between w-2/5 md:w-full items-center">
          <div>
            <div className="md:flex items-center">
              <div className="mr-4 hidden md:block overflow-hidden rounded-full w-12 h-12">
                {entry.doctorImage && (
                  <Image
                    src={entry.doctorImage.url}
                    alt={
                      entry.doctorImage.description ||
                      entry.doctorImage.title ||
                      'doctor'
                    }
                    width={48}
                    height={48}
                    fit="thumb"
                    focus="face"
                    contentful
                    className="object-cover"
                  />
                )}
              </div>
              <p className="text-navy font-bold truncate whitespace-nowrap">
                {entry.title} {entry.firstName} {entry.lastName}
              </p>
              <div>
                {shouldReviewsBeVisible && (
                  <p className="flex items-center md:ml-4">
                    <span className="font-bold mr-1 text-tertiary md:text-sm order-2 md:order-1">
                      {(reviewStats?.avg_rating || 0).toFixed(1)}
                    </span>{' '}
                    <Icon type="Star" className="mr-2 order-1 md:order-2" />
                    <span className="md:text-xs text-charcoal md:text-qunoGrey order-3">
                      ({reviewStats.total})
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
          {startPackage && (
            <div className="text-siam hidden md:block">
              {t('packages')}{' '}
              <span className="font-bold">
                {t('startingFrom')}{' '}
                {formatCurrency(startPackage.price, 'EUR', locale)}
              </span>
            </div>
          )}
        </div>
      </ConversionBar>
    </>
  );
};

export default DoctorProfile;
